import { Component, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MapServiceFactory } from '../../services/maps-service-factory';
import { MapResponse } from '../../../core/maps-api';
import { combineLatest, ReplaySubject, Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { FormatMapErrorPipe } from '../../pipes/format/format-map-error.pipe';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { GbGrundstueckListEntryMeta, GrundstueckDetailEntryMeta, GrundstueckListEntry, HistInfo } from '../../../core/gb-api';
import { MapType } from '../../enum/map-type';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ErweiterungConfigResponse } from '../../../core/base-api';
import HistEnumEnum = HistInfo.HistEnumEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

type ParameterConfig = { [p: string]: object };

@Component({
  selector: 'cap-maps[mapType][model]',
  templateUrl: './maps.component.html',
  styleUrls: ['./maps.component.scss'],
  imports: [NgbTooltip, TranslatePipe]
})
export class MapsComponent implements OnInit, OnDestroy {
  @Input()
  mapType: MapType;
  @Input()
  tooltip: string;
  @Input()
  model: any;

  isTopomaps: boolean = false;
  mapLoaded: boolean = false;
  mapSubject = new ReplaySubject<boolean>();

  @Output()
  mapVerfuegbar = this.mapSubject.asObservable();

  private subscription: Subscription = new Subscription();
  private subscriptionMap: Subscription;
  private parameterConfig: ParameterConfig;

  constructor(
    private serviceFactory: MapServiceFactory,
    private appFormatConfigService: FormatConfigService,
    private translateService: TranslateService,
    private capAlertService: CapAlertService,
    private formatErrorPipe: FormatMapErrorPipe
  ) {
    keysToTranslate<string>([
      'GEO_LINK',
      'TOPOMAPS',
      'maps.anzeigePlan',
      'maps.anzeigePlanZu',
      'maps.exception.errorPageTitle',
      'maps.exception.geo_link.allGrundstueckeNotFound',
      'maps.exception.geo_link.authorizationFailed',
      'maps.exception.geo_link.errorMessage',
      'maps.exception.geo_link.gemeindeNotFound',
      'maps.exception.geo_link.grundstueckNotFound',
      'maps.exception.geo_link.invalidRequest',
      'maps.exception.topomaps.authorizationFailed',
      'maps.exception.topomaps.errorMessage',
      'maps.exception.topomaps.invalidRequest'
    ]);
  }

  ngOnInit(): void {
    this.subscription.add(
      combineLatest([
        this.appFormatConfigService.erweiterungsConfig$,
        this.appFormatConfigService.settingsConfig$,
        this.appFormatConfigService.parameterConfig$
      ]).subscribe(([erweiterungsConfig, settingsConfig, parameterConfig]) => {
        this.isTopomaps = erweiterungsConfig?.activeErweiterungen.includes(ActiveErweiterungenEnum.GEOLINK_VIA_TOPOMAPS);
        if (!settingsConfig?.isMapsLinkConfigured) {
          this.setMapLoaded(false);
        }
        this.parameterConfig = parameterConfig?.parameters;
        if (this.model) {
          this.initializeMap();
        }
      })
    );
    this.subscription.add(this.translateService.onLangChange.subscribe(() => this.initializeMap()));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (this.subscriptionMap) {
      this.subscriptionMap.unsubscribe();
    }
  }

  private setMapLoaded(value: boolean) {
    this.mapLoaded = value;
    this.mapSubject.next(value);
  }

  @HostListener('click', ['$event.target'])
  onClick(target: never): void {
    this.subscriptionMap = this.serviceFactory.createFrom(this.mapType, this.model).subscribe({
      next: (response: MapResponse) => {
        if (response) {
          const url: string = response.url;
          if (url) {
            window.open(url, '_blank');
            return;
          } else {
            this.capAlertService.warning(this.translateService.instant('ErrorGettingMap'));
          }
        }
      },
      error: (error) => {
        this.setMapLoaded(false);
        if (error && !(error.status && error.status === 404) && error.error) {
          this.capAlertService.warning(this.formatErrorPipe.transform(error.error));
        }
      }
    });
  }

  initializeMap(): void {
    const histInfo = this.model ? this.model.histInfo : '';
    if (this.isEmptyTypeOrModel() || this.isNotActual(histInfo) || this.isNoValidMapType()) {
      this.setMapLoaded(false);
      return;
    }
    if (this.mapType === MapType.grundstueck) {
      if (
        !this.parameterFuerGsArtAktiviert(this.model as GrundstueckListEntry | GbGrundstueckListEntryMeta | GrundstueckDetailEntryMeta) ||
        (this.model as GrundstueckListEntry | GbGrundstueckListEntryMeta | GrundstueckDetailEntryMeta).isKopie
      ) {
        this.setMapLoaded(false);
        return;
      }
    }
    this.setMapLoaded(true);
  }

  private parameterFuerGsArtAktiviert(
    grundstueck: GrundstueckListEntry | GbGrundstueckListEntryMeta | GrundstueckDetailEntryMeta
  ): boolean {
    if (!grundstueck) {
      return false;
    }
    if (!this.parameterConfig) {
      return;
    }
    let isAktiviert: boolean = false;
    switch (grundstueck.grundstueckArt) {
      case 'LIEGENSCHAFT':
        isAktiviert = !!this.parameterConfig.LIEGENSCHAFT_MIT_GEO_LINK;
        break;
      case 'SDR':
        if (grundstueck.flaechenMaessigNichtAusgeschieden) {
          isAktiviert = !!this.parameterConfig.SDR_NICHT_AUSGESCHIEDEN_MIT_GEO_LINK;
        } else {
          isAktiviert = !!this.parameterConfig.SDR_AUSGESCHIEDEN_MIT_GEO_LINK;
        }
        break;
      case 'STOCKWERK':
        isAktiviert = !!this.parameterConfig.STOCKWERKEINHEIT_MIT_GEO_LINK;
        break;
      case 'MITEIGENTUM':
        isAktiviert = !!this.parameterConfig.MITEIGENTUM_MIT_GEO_LINK;
        break;
      case 'BERGWERK':
        isAktiviert = !!this.parameterConfig.BERGWERK_MIT_GEO_LINK;
        break;
      case 'FOLIO':
        isAktiviert = !!this.parameterConfig.FOLIO_MIT_GEO_LINK;
        break;
    }
    return isAktiviert;
  }

  private isNoValidMapType(): boolean {
    return !Object.values(MapType).includes(this.mapType) || (!this.isTopomaps && !this.isValidGeolinkMapType());
  }

  private isValidGeolinkMapType(): boolean {
    return this.mapType === MapType.grundstueck;
  }

  private isNotActual(histInfo: any): boolean {
    return histInfo && HistEnumEnum.VON_RECHTGUELTIG_BIS_RECHTSGUELTIG === histInfo.histEnum;
  }

  private isEmptyTypeOrModel(): boolean {
    return !this.mapType && !this.model;
  }
}
